// Editable coverage schedule for the demo.
//
// The per-day hours in mockData are the seed; a change made here lives in
// memory for the session and resets on reload, matching the knowledge bank's
// edits. It is a module-level store rather than component state so the hours
// stay changed while navigating between the call list and the knowledge bank.
//
// Nothing downstream reads this schedule — no fixture call is filtered or
// re-bucketed by it — so editing it changes what the line says about itself,
// not the calls it already handled.

import { useSyncExternalStore } from "react";
import { DEMO_LINE_CONTEXT } from "./mockData";
import { normalizeCoverageSchedule } from "./selectors";
import { CoverageSchedule } from "./types";

const SEED_SCHEDULE = DEMO_LINE_CONTEXT.coverageSchedule;

let coverageSchedule: CoverageSchedule = SEED_SCHEDULE;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCoverageSchedule(): CoverageSchedule {
  return coverageSchedule;
}

export function useCoverageSchedule(): CoverageSchedule {
  return useSyncExternalStore(subscribe, getCoverageSchedule);
}

export function setCoverageSchedule(next: CoverageSchedule): void {
  coverageSchedule = normalizeCoverageSchedule(next);
  listeners.forEach((listener) => listener());
}

export function resetCoverageSchedule(): void {
  coverageSchedule = SEED_SCHEDULE;
  listeners.forEach((listener) => listener());
}

/** True once the hours differ from the seed, so the UI can offer a reset. */
export function isCoverageScheduleEdited(schedule: CoverageSchedule): boolean {
  return JSON.stringify(schedule) !== JSON.stringify(SEED_SCHEDULE);
}
